import { checkIsLineOfCreditOrMortgageTypeOfCredit } from 'app/pages/loanApplication/LoanApplication.utils';
import {
  ALL_BORROWERS_PATH,
  FINANCIAL_INFO_TABLE_KEY,
  FINANCIAL_INFO_VIEWS_PATHS,
  NEW_RECORD_DATA,
} from 'app/pages/loanApplication/tabs/financialInfo/FinancialInfo.constants';
import {
  isEmpty,
  isEqualWith,
  isNil,
  keyBy,
  mapValues,
  memoize,
  omit,
  orderBy,
  set,
  sortBy,
} from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import {
  AssetsType,
  EmployerPhoneSource,
  extendedIncomeTypes,
  IncomeType,
  LiabilitiesType,
  SalaryType,
  TypeOfCredit,
} from 'constants/loanApplication';
import {
  PendingChangesActionType,
  PendingChangesTypeToHistoryTypeMap,
} from 'constants/pendingChanges';
import { getConditionalArray, getSplittedAddress } from 'utils/data';
import { equalCustomizer } from 'utils/form';
import { getBorrowerFullName } from 'utils/loanApplication';
import { roundToDecimals } from 'utils/number';
import { getApplicableAddressFieldsPendingChanges } from 'utils/pendingChanges';

import { selectLoanApplicationFinancialInfo } from './loanApplication.selectors';
import {
  selectLoanApplicationProperties,
  selectLoanApplicationPropertiesByLiabilityId,
} from './loanApplicationProperties.selectors';
import { selectEntitiesChangedPropsByIds } from './loanChangesHistory.selectors';

const joinArgs = (...args) => args.join('-');

const selectFinancialInfo = createSelector(
  selectLoanApplicationFinancialInfo,
  financialInfo => financialInfo,
);

const selectFinancialInfoSummary = createSelector(selectFinancialInfo, ({ summary }) => ({
  ...summary,
  borrowers: summary?.borrowers?.map(borrower => ({
    ...borrower,
    fullName: getBorrowerFullName(borrower.borrowerFullName),
  })),
}));

const selectFinancialInfoAssets = createSelector(selectFinancialInfo, ({ assets }) => assets ?? []);

const selectFinancialInfoLiabilities = createSelector(
  selectFinancialInfo,
  ({ liabilities }) => liabilities ?? [],
);

const selectFinancialInfoIncomes = createSelector(
  selectFinancialInfo,
  ({ incomes }) => incomes ?? [],
);

const selectFinancialInfoProperties = createSelector(
  selectFinancialInfo,
  ({ properties }) => properties ?? [],
);

export const selectFinancialInfoPropertyAddressById = createSelector(
  selectFinancialInfoProperties,
  properties => mapValues(keyBy(properties, 'id'), 'address'),
);

export const selectIsClosedHidden = createSelector(
  selectFinancialInfo,
  ({ isClosedHidden }) => isClosedHidden,
);

export const selectFinancialInfoAllTablesData = createSelector(
  [
    selectFinancialInfoAssets,
    selectFinancialInfoLiabilities,
    selectFinancialInfoIncomes,
    selectFinancialInfoProperties,
  ],
  (assets, liabilities, incomes, properties) => ({
    [FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.ASSETS]]: assets,
    [FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES]]: liabilities,
    [FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT]]: incomes,
    [FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.REO]]: properties,
  }),
);

export const selectFinancialInfoBorrowers = createSelector(
  [selectFinancialInfoSummary, selectFinancialInfoAllTablesData],
  ({ borrowers }, allTablesData) =>
    sortBy(borrowers ?? [], borrower => borrower.orderNumber).map(borrower => {
      const { firstName, lastName } = borrower.borrowerFullName;

      const records = Object.values(allTablesData)
        .flat()
        .filter(
          ({ borrowerId, owners, ownerIds }) =>
            borrowerId === borrower.id || (ownerIds ?? owners ?? []).includes(borrower.id),
        );

      return {
        ...borrower,
        records,
        firstName,
        lastName,
      };
    }),
);

export const selectFinancialInfoOwnersOptions = createSelector(
  selectFinancialInfoBorrowers,
  borrowers => borrowers.map(({ fullName, id }) => ({ name: fullName, value: id })),
);

const selectFinancialInfoBorrowersMap = createSelector(
  selectFinancialInfoBorrowers,
  borrowers => new Map(borrowers.map(borrower => [borrower.id, borrower])),
);

export const selectFinancialInfoCashToClose = createSelector(
  selectFinancialInfoSummary,
  ({ cashToClose }) => cashToClose ?? '0',
);

export const selectFinancialInfoProjectedReserves = createSelector(
  selectFinancialInfoSummary,
  ({ projectedReserves }) => projectedReserves ?? '0',
);

export const selectFinancialInfoSummaryPendingFields = createSelector(
  selectFinancialInfoSummary,
  ({ modifiedFields, modifiedFieldDetails }) => ({
    modifiedFields: modifiedFields ?? [],
    modifiedFieldDetails: modifiedFieldDetails ?? [],
  }),
);

export const selectFinancialInfoDebtToIncome = createSelector(
  selectFinancialInfoSummary,
  ({ debtToIncome }) => debtToIncome ?? '0',
);

const selectFinancialInfoCurrentFinancials = createSelector(
  selectFinancialInfoSummary,
  ({ currentFinancials }) => currentFinancials || {},
);

export const selectFinancialInfoCurrentFinancialsBorrowerAssets = memoizeOne(borrowerId =>
  createSelector(
    selectFinancialInfoCurrentFinancials,
    ({ currentAssetsByBorrower, totalAssets }) => {
      if (borrowerId === ALL_BORROWERS_PATH) {
        return totalAssets;
      }

      return currentAssetsByBorrower?.[borrowerId] || 0;
    },
  ),
);

export const selectFinancialInfoCurrentFinancialsBorrowerIncomes = memoizeOne(borrowerId =>
  createSelector(
    selectFinancialInfoCurrentFinancials,
    ({ currentIncomesByBorrower, totalIncomes }) => {
      const value =
        borrowerId === ALL_BORROWERS_PATH ? totalIncomes : currentIncomesByBorrower?.[borrowerId];

      return roundToDecimals(value || 0);
    },
  ),
);

export const selectFinancialInfoCurrentFinancialsBorrowerLiabilities = memoizeOne(borrowerId =>
  createSelector(
    selectFinancialInfoCurrentFinancials,
    ({ currentLiabilitiesByBorrower, totalLiabilities }) => {
      if (borrowerId === ALL_BORROWERS_PATH) {
        return totalLiabilities;
      }

      return currentLiabilitiesByBorrower?.[borrowerId] || 0;
    },
  ),
);

export const selectFinancialInfoCurrentFinancialsBorrowerNegativeRentalIncome = memoizeOne(
  borrowerId =>
    createSelector(
      selectFinancialInfoCurrentFinancials,
      ({ currentNegativeRentalIncomeByBorrower, totalNegativeRentalIncomes }) => {
        if (borrowerId === ALL_BORROWERS_PATH) {
          return Math.abs(totalNegativeRentalIncomes);
        }

        return Math.abs(currentNegativeRentalIncomeByBorrower?.[borrowerId] || 0);
      },
    ),
);

const selectSortedFinancialInfoLiabilities = isEditMode =>
  createSelector(
    [selectFinancialInfoLiabilities, selectFinancialInfoBorrowers],
    (liabilities, borrowers) => {
      const primaryBorrowerId = borrowers.find(borrower => borrower.orderNumber === 1)?.id;

      const liabilitiesWithOrderNumber = liabilities.map(liability => ({
        ...liability,
        orderNumber: liability.owners.includes(primaryBorrowerId) ? 1 : Infinity,
        ownersInfo: borrowers.filter(({ id }) => liability.owners.includes(id)),
      }));

      return orderBy(
        liabilitiesWithOrderNumber,
        [
          item => !isEditMode || Number(item.unpaidBalance),
          item => item.orderNumber,
          item => Number(item.monthlyPayment),
        ],
        ['desc', 'asc', 'desc'],
      );
    },
  );

export const selectSortedBorrowerLiabilities = memoize(
  (borrowerId, isEditMode = false) =>
    createSelector(selectSortedFinancialInfoLiabilities(isEditMode), liabilities => {
      if (borrowerId === ALL_BORROWERS_PATH) {
        return liabilities;
      }

      return liabilities.filter(liability => liability.owners.includes(borrowerId));
    }),
  joinArgs,
);

const selectSortedFinancialInfoIncomes = isEditMode =>
  createSelector(
    [selectFinancialInfoIncomes, selectFinancialInfoBorrowersMap],
    (incomes, borrowersMap) => {
      const incomesWithOrderNumber = incomes.map(income => {
        const borrower = borrowersMap.get(income.borrowerId);

        return {
          ...income,
          orderNumber: borrower?.orderNumber === 1 ? 1 : Infinity,
          borrower,
        };
      });

      return orderBy(
        incomesWithOrderNumber,
        [item => isEditMode || item.orderNumber, item => Number(item.monthlyIncome)],
        ['asc', 'desc'],
      );
    },
  );

const selectSortedFinancialInfoProperties = (borrowerId, sortByAddress) =>
  createSelector(
    [selectFinancialInfoProperties, selectFinancialInfoBorrowersMap],
    (properties, borrowersMap) => {
      const propertiesWithOrderNumber = properties.map(property => ({
        ...property,
        netIncome: property.netIncome || 0,
        orderNumber:
          borrowerId === ALL_BORROWERS_PATH &&
          property.owners.some(id => borrowersMap.get(id)?.orderNumber === 1)
            ? 1
            : Infinity,
      }));

      return orderBy(
        propertiesWithOrderNumber,
        [
          item => sortByAddress || item.orderNumber,
          item => sortByAddress || Number(item.netIncome),
          'address',
        ],
        ['asc', 'desc', 'asc'],
      );
    },
  );

const selectSortedFinancialInfoAssets = (borrowerId, isEditMode) =>
  createSelector([selectFinancialInfoAssets, selectFinancialInfoBorrowers], (assets, borrowers) => {
    const primaryBorrowerId = borrowers.find(borrower => borrower.orderNumber === 1)?.id;

    const assetsWithOrderNumber = assets.map(asset => ({
      ...asset,
      orderNumber:
        asset.ownerIds.includes(primaryBorrowerId) && borrowerId === ALL_BORROWERS_PATH
          ? 1
          : Infinity,
      ownersInfo: borrowers.filter(({ id }) => asset.ownerIds.includes(id)),
    }));

    return orderBy(
      assetsWithOrderNumber,
      [item => isEditMode || item.orderNumber, item => Number(item.balance)],
      ['asc', 'desc'],
    );
  });

export const selectSortedBorrowerIncomes = memoize(
  (borrowerId, isEditMode = false) =>
    createSelector(selectSortedFinancialInfoIncomes(isEditMode), incomes => {
      if (borrowerId === ALL_BORROWERS_PATH) {
        return incomes;
      }

      return incomes.filter(income => income.borrowerId === borrowerId);
    }),
  joinArgs,
);

export const selectSortedBorrowerProperties = memoize(
  (borrowerId, isEditMode = false) =>
    createSelector(selectSortedFinancialInfoProperties(borrowerId, isEditMode), properties => {
      const propertiesWithSplittedAddress = properties.map(property => {
        const [addressLine1, addressLine2] = getSplittedAddress(property.address);

        return {
          ...property,
          addressLine1,
          addressLine2,
        };
      });

      if (borrowerId === ALL_BORROWERS_PATH) {
        return propertiesWithSplittedAddress;
      }

      return propertiesWithSplittedAddress.filter(property => property.owners.includes(borrowerId));
    }),
  joinArgs,
);

export const selectSortedBorrowerAssets = memoize(
  (borrowerId, isEditMode = false) =>
    createSelector(selectSortedFinancialInfoAssets(borrowerId, isEditMode), assets => {
      if (borrowerId === ALL_BORROWERS_PATH) {
        return assets;
      }

      return assets.filter(asset => asset.ownerIds.includes(borrowerId));
    }),
  joinArgs,
);

const getNewRecordsPendingChanges = pendingChanges =>
  Object.entries(pendingChanges).reduce((acc, [recordId, recordPendingChanges]) => {
    if (recordId.startsWith(NEW_RECORD_DATA.id)) {
      acc[recordId] = recordPendingChanges;
    }

    return acc;
  }, {});

const checkIsAssetHasGiftOrGrantType = type =>
  type === AssetsType.GIFT || type === AssetsType.GRANT;

const checkIsAssetIncludedInAssets = (assetPendingChanges, savedAsset = null) => {
  const assetTypeNewValue = assetPendingChanges?.type?.newValue;
  const includeInAssetsNewValue = assetPendingChanges?.includeInAssets?.newValue;

  const isAssetTypeChanged = assetTypeNewValue !== undefined;
  const isIncludeInAssetsChanged = includeInAssetsNewValue !== undefined;

  const isGiftOrGrantType = isAssetTypeChanged
    ? checkIsAssetHasGiftOrGrantType(assetTypeNewValue)
    : checkIsAssetHasGiftOrGrantType(savedAsset?.assetTypeName);

  return (
    !isGiftOrGrantType ||
    (isIncludeInAssetsChanged ? includeInAssetsNewValue : savedAsset?.includeInAssets)
  );
};

const getSumOfAssetsFromNewRecords = (pendingChanges = {}) =>
  Object.entries(getNewRecordsPendingChanges(pendingChanges)).reduce(
    (acc, [__, newIncomePendingChanges]) => {
      if (!checkIsAssetIncludedInAssets(newIncomePendingChanges)) {
        return acc;
      }

      return acc + (Number(newIncomePendingChanges?.cashOrMarketValue?.newValue) || 0);
    },
    0,
  );

export const selectFinancialInfoTotalAssets = ({ borrowerId, pendingChanges = {} }) =>
  createSelector(selectFinancialInfoAssets, assets =>
    roundToDecimals(
      assets.reduce((acc, asset) => {
        const currentAssetPendingChanges = pendingChanges[asset.id];

        const isDeleted =
          asset.pendingChangesActionType === PendingChangesActionType.DELETED ||
          currentAssetPendingChanges?.pendingChangesActionType === PendingChangesActionType.DELETED;

        if (isDeleted) {
          return acc;
        }

        const isIncludedInAssets = checkIsAssetIncludedInAssets(currentAssetPendingChanges, asset);

        if (
          (borrowerId === ALL_BORROWERS_PATH || asset.ownerIds.includes(borrowerId)) &&
          isIncludedInAssets
        ) {
          return (
            acc + Number(currentAssetPendingChanges?.cashOrMarketValue?.newValue ?? asset.balance)
          );
        }

        return acc;
      }, getSumOfAssetsFromNewRecords(pendingChanges)),
    ),
  );

const checkIsLiabilityExcludedFromCalculations = (
  liabilityPendingChanges,
  savedLiability = null,
) => {
  const liabilityTypePendingChangeNewValue = liabilityPendingChanges?.type?.newValue;
  const toBePaidOffPendingChangeNewValue =
    liabilityPendingChanges?.toBePaidOffAtOrBeforeClosing?.newValue ??
    liabilityPendingChanges?.liabilityLinkedToProperty?.toBePaidOffAtOrBeforeClosing?.newValue;
  const excludePaymentPendingChangeNewValue =
    liabilityPendingChanges?.excludePayment?.newValue ??
    liabilityPendingChanges?.liabilityLinkedToProperty?.excludePayment?.newValue;
  const accountTypePendingChangeNewValue = liabilityPendingChanges?.accountType?.newValue;
  const unpaidBalancePendingChangeNewValue = liabilityPendingChanges?.unpaidBalance?.newValue;

  const isLiabilityTypeChanged = liabilityTypePendingChangeNewValue !== undefined;

  const isChangedLiabilityTypeLiability =
    liabilityTypePendingChangeNewValue === LiabilitiesType.LIABILITY;

  const isPayoffRequired = isLiabilityTypeChanged
    ? isChangedLiabilityTypeLiability && toBePaidOffPendingChangeNewValue
    : (toBePaidOffPendingChangeNewValue ?? savedLiability?.payoffRequired);

  const isExcludePayment = isLiabilityTypeChanged
    ? isChangedLiabilityTypeLiability && excludePaymentPendingChangeNewValue
    : (excludePaymentPendingChangeNewValue ?? savedLiability?.exclude);

  const isAccountTypeOpenThirtyDay = isLiabilityTypeChanged
    ? isChangedLiabilityTypeLiability &&
      accountTypePendingChangeNewValue === TypeOfCredit.OPEN_30_DAY
    : (accountTypePendingChangeNewValue ?? savedLiability?.liabilityAccountType) ===
      TypeOfCredit.OPEN_30_DAY;

  const isUnpaidBalance = isLiabilityTypeChanged
    ? isChangedLiabilityTypeLiability && unpaidBalancePendingChangeNewValue
    : Number(unpaidBalancePendingChangeNewValue ?? savedLiability?.unpaidBalance) || 0;

  const accountType = accountTypePendingChangeNewValue ?? savedLiability?.liabilityAccountType;
  const hasPropertyPendingChange = !isNil(liabilityPendingChanges?.property);
  const propertyPendingChangeNewValue = liabilityPendingChanges?.property?.newValue;
  const isPropertyLinked = hasPropertyPendingChange
    ? !!propertyPendingChangeNewValue
    : !!savedLiability?.propertyId;
  const isPropertyAssociatedLiability =
    isPropertyLinked && checkIsLineOfCreditOrMortgageTypeOfCredit(accountType);

  return [
    isPayoffRequired,
    isExcludePayment,
    isAccountTypeOpenThirtyDay,
    !isUnpaidBalance,
    isPropertyAssociatedLiability,
  ].some(Boolean);
};

const getSumOfLiabilitiesFromNewRecords = (pendingChanges = {}) =>
  Object.entries(getNewRecordsPendingChanges(pendingChanges)).reduce(
    (acc, [__, newIncomePendingChanges]) => {
      if (checkIsLiabilityExcludedFromCalculations(newIncomePendingChanges)) {
        return acc;
      }

      return acc + (Number(newIncomePendingChanges?.monthlyPayment?.newValue) || 0);
    },
    0,
  );

export const selectFinancialInfoTotalLiabilities = ({ borrowerId, pendingChanges = {} }) =>
  createSelector(selectFinancialInfoLiabilities, liabilities =>
    roundToDecimals(
      liabilities.reduce((acc, liability) => {
        const currentLiabilityPendingChanges = pendingChanges[liability.id];

        const isDeleted =
          liability.pendingChangesActionType === PendingChangesActionType.DELETED ||
          currentLiabilityPendingChanges?.pendingChangesActionType ===
            PendingChangesActionType.DELETED;

        if (isDeleted) {
          return acc;
        }

        const isLiabilityExcludedFromCalculations = checkIsLiabilityExcludedFromCalculations(
          currentLiabilityPendingChanges,
          liability,
        );

        if (
          !isLiabilityExcludedFromCalculations &&
          (borrowerId === ALL_BORROWERS_PATH || liability.owners.includes(borrowerId))
        ) {
          return (
            acc +
            Number(
              currentLiabilityPendingChanges?.monthlyPayment?.newValue ?? liability.monthlyPayment,
            )
          );
        }

        return acc;
      }, getSumOfLiabilitiesFromNewRecords(pendingChanges)),
    ),
  );

const selectFinancialInfoTotalRentalIncome = ({ borrowerId }) =>
  createSelector(selectFinancialInfoProperties, properties =>
    properties.reduce((acc, property) => {
      const isDeleted = property.pendingChangesActionType === PendingChangesActionType.DELETED;

      if (isDeleted) {
        return acc;
      }

      if (borrowerId === ALL_BORROWERS_PATH || property.owners.includes(borrowerId)) {
        const netIncome = Number(property.netIncome);

        return netIncome > 0 ? acc + netIncome : acc;
      }

      return acc;
    }, 0),
  );

const getIncomesMonthlyIncomes = (recordPendingChanges, monthlyIncome = 0) => {
  switch (true) {
    case !!recordPendingChanges.salary:
      return Object.values(recordPendingChanges.salary).reduce(
        (acc, { value }) => acc + Number(value?.newValue ?? 0),
        0,
      );
    case !!recordPendingChanges[SalaryType.BASE_SALARY]:
      return Number(recordPendingChanges[SalaryType.BASE_SALARY].newValue) || 0;

    case !!recordPendingChanges.monthlyIncome:
      return Number(recordPendingChanges.monthlyIncome?.newValue) || 0;

    default:
      return Number(monthlyIncome);
  }
};

const checkIsIncomeEnded = (recordPendingChanges, endDate = null) => {
  switch (true) {
    case recordPendingChanges?.endDate?.newValue !== undefined:
      return !!recordPendingChanges.endDate.newValue;

    case recordPendingChanges?.isCurrentlyEmployedHere?.newValue !== undefined:
      return !recordPendingChanges.isCurrentlyEmployedHere.newValue;

    default:
      return recordPendingChanges?.incomeType?.newValue !== undefined &&
        !extendedIncomeTypes.includes(recordPendingChanges.incomeType.newValue)
        ? false
        : !!endDate;
  }
};

const getSumOfIncomesFromNewRecords = (pendingChanges = {}) =>
  Object.entries(getNewRecordsPendingChanges(pendingChanges)).reduce(
    (acc, [__, newIncomePendingChanges]) => {
      if (checkIsIncomeEnded(newIncomePendingChanges)) {
        return acc;
      }

      return acc + getIncomesMonthlyIncomes(newIncomePendingChanges);
    },
    0,
  );

export const selectFinancialInfoTotalIncomes = ({ borrowerId, pendingChanges = {} }) =>
  createSelector(
    [selectFinancialInfoIncomes, selectFinancialInfoTotalRentalIncome({ borrowerId })],
    (incomes, totalRentalIncome) =>
      roundToDecimals(
        incomes.reduce((acc, income) => {
          const currentIncomePendingChanges = pendingChanges[income.id];

          const isDeleted =
            income.pendingChangesActionType === PendingChangesActionType.DELETED ||
            currentIncomePendingChanges?.pendingChangesActionType ===
              PendingChangesActionType.DELETED;
          const isEnded = currentIncomePendingChanges
            ? checkIsIncomeEnded(currentIncomePendingChanges, income.endDate)
            : income.endDate;

          if (isDeleted || isEnded) {
            return acc;
          }

          if (borrowerId === ALL_BORROWERS_PATH || income.borrowerId === borrowerId) {
            return (
              acc +
              (currentIncomePendingChanges
                ? getIncomesMonthlyIncomes(currentIncomePendingChanges, income.monthlyIncome)
                : Number(income.monthlyIncome))
            );
          }

          return acc;
        }, 0) +
          totalRentalIncome +
          getSumOfIncomesFromNewRecords(pendingChanges),
      ),
  );

export const selectFinancialInfoTotalNegativeRentalIncome = ({ borrowerId }) =>
  createSelector(selectFinancialInfoProperties, properties =>
    roundToDecimals(
      properties.reduce((acc, property) => {
        const isDeleted = property.pendingChangesActionType === PendingChangesActionType.DELETED;

        if (isDeleted) {
          return acc;
        }

        if (borrowerId === ALL_BORROWERS_PATH || property.owners.includes(borrowerId)) {
          const netIncome = Number(property.netIncome);

          return netIncome < 0 ? acc + Math.abs(netIncome) : acc;
        }

        return acc;
      }, 0),
    ),
  );

export const selectFinancialInfoPendingChanges = createSelector(
  [selectFinancialInfoAllTablesData, selectFinancialInfoBorrowers, state => state],
  (allTablesData, borrowers, state) => {
    return Object.entries(allTablesData).reduce((acc, [key, records]) => {
      const { recordsIds, recordsWithOuterPendingChanges } = records.reduce(
        (recordsAcc, recordData) => {
          const {
            id: recordId,
            addressId,
            previousAddressId,
            owners,
            ownerIds,
            borrowerId,
            pendingChangesActionType,
          } = recordData;

          const relatedOwnersWithRecordsPendingChangesIds = borrowers.map(
            ({ id }) => `${id}_${recordId}`,
          );

          const relatedBorrowersWithRecordPendingChanges = selectEntitiesChangedPropsByIds(
            relatedOwnersWithRecordsPendingChangesIds,
          )(state);

          const ownersNewValue = [...(owners ?? ownerIds ?? [borrowerId])];

          const ownersInitialValue = Object.values(relatedBorrowersWithRecordPendingChanges).reduce(
            (acc, pendingChanges) => {
              const { borrowerId } = pendingChanges;

              if (!borrowerId) {
                return acc;
              }

              const { newValue, initialValue } = borrowerId;

              return newValue === null ? [...acc, initialValue] : acc.filter(id => id !== newValue);
            },
            [...ownersNewValue],
          );

          const isOwnersPendingChangeHasDifference = !isEqualWith(
            ownersInitialValue,
            ownersNewValue,
            equalCustomizer,
          );

          if (isOwnersPendingChangeHasDifference) {
            set(recordsAcc, `recordsWithOuterPendingChanges[${recordId}].owners`, {
              newValue: ownersNewValue,
              initialValue: ownersInitialValue,
              actionType: PendingChangesTypeToHistoryTypeMap[pendingChangesActionType],
            });
          }

          if (key === FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.LIABILITIES]) {
            const propertiesLinkedToLiability =
              selectLoanApplicationPropertiesByLiabilityId(recordId)(state);
            const propertiesIds = propertiesLinkedToLiability?.map(({ value }) => value) || [];

            const propertiesPendingChanges = selectEntitiesChangedPropsByIds(propertiesIds)(state);

            const relatedPropertiesWithChanges = Object.entries(propertiesPendingChanges).reduce(
              (acc, [propertyId, pendingChanges]) => {
                ['firstMortgageId', 'secondMortgageId'].forEach(mortgageKey => {
                  const mortgage = pendingChanges[mortgageKey];

                  if (
                    mortgage?.newValue !== mortgage?.initialValue &&
                    (mortgage.newValue === recordId || mortgage.initialValue === recordId)
                  ) {
                    acc.push([propertyId, mortgage]);
                  }
                });

                return acc;
              },
              [],
            );

            if (relatedPropertiesWithChanges.length > 0) {
              const formattedPropertyPendingChange = {
                initialValue:
                  relatedPropertiesWithChanges.find(
                    ([__, { initialValue }]) => initialValue === recordId,
                  )?.[0] ?? null,
                newValue:
                  relatedPropertiesWithChanges.find(
                    ([__, { newValue }]) => newValue === recordId,
                  )?.[0] ?? null,
                actionType: PendingChangesTypeToHistoryTypeMap[pendingChangesActionType],
              };

              set(
                recordsAcc,
                `recordsWithOuterPendingChanges[${recordId}].property`,
                formattedPropertyPendingChange,
              );
            }
          }

          if (key === FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.INCOME_AND_EMPLOYMENT]) {
            const isSelfEmployedIncomeType = recordData.incomeType === IncomeType.SELF_EMPLOYED;
            const isW2IncomeType = recordData.incomeType === IncomeType.W2;
            const employmentVerificationPendingChanges =
              isSelfEmployedIncomeType || isW2IncomeType
                ? selectEntitiesChangedPropsByIds(recordData.employmentVerificationIds || [])(state)
                : {};

            if (!isEmpty(employmentVerificationPendingChanges)) {
              const employmentVerificationPendingChangesToSet = mapValues(
                employmentVerificationPendingChanges,
                employmentVerification => {
                  return {
                    ...omit(employmentVerification, [
                      ...getConditionalArray(isW2IncomeType, ['businessExistenceVerified']),
                      ...getConditionalArray(
                        employmentVerification.employerPhoneSource?.newValue !==
                          EmployerPhoneSource.OTHER,
                        ['otherEmployerPhoneSourceDescription'],
                      ),
                    ]),
                    isSeparateChange: true,
                  };
                },
              );

              set(
                recordsAcc,
                `recordsWithOuterPendingChanges[${recordId}].employmentVerifications`,
                employmentVerificationPendingChangesToSet,
              );
            }
          }

          if (key === FINANCIAL_INFO_TABLE_KEY[FINANCIAL_INFO_VIEWS_PATHS.REO]) {
            const currentPropertyValues =
              keyBy(selectLoanApplicationProperties(state) || [], 'id')[recordId] ?? {};

            const { firstMortgageId, secondMortgageId } = currentPropertyValues?.mortgages ?? {};

            if (firstMortgageId) {
              const firstMortgagePendingChanges =
                selectEntitiesChangedPropsByIds(firstMortgageId)(state)[firstMortgageId];

              set(
                recordsAcc,
                `recordsWithOuterPendingChanges[${recordId}].firstMortgage`,
                firstMortgagePendingChanges,
              );
            }

            if (secondMortgageId) {
              const secondMortgagePendingChanges =
                selectEntitiesChangedPropsByIds(secondMortgageId)(state)[secondMortgageId];

              set(
                recordsAcc,
                `recordsWithOuterPendingChanges[${recordId}].secondMortgage`,
                secondMortgagePendingChanges,
              );
            }
          }

          recordsAcc.recordsIds.push(recordId);

          if (addressId || previousAddressId) {
            const outerPendingChanges = selectEntitiesChangedPropsByIds([
              ...getConditionalArray(addressId, [addressId]),
              ...getConditionalArray(previousAddressId, [previousAddressId]),
            ])(state);

            const { [addressId]: address, [previousAddressId]: previousAddress } =
              outerPendingChanges;

            if (address) {
              set(
                recordsAcc,
                `recordsWithOuterPendingChanges[${recordId}].address`,
                getApplicableAddressFieldsPendingChanges(address),
              );
            }

            if (previousAddress) {
              set(
                recordsAcc,
                `recordsWithOuterPendingChanges[${recordId}].previousAddress`,
                getApplicableAddressFieldsPendingChanges(previousAddress),
              );
            }
          }

          return recordsAcc;
        },
        { recordsIds: [], recordsWithOuterPendingChanges: {} },
      );

      acc[key] = selectEntitiesChangedPropsByIds(recordsIds)(state);

      acc[key] = {
        ...acc[key],
        ...Object.entries(recordsWithOuterPendingChanges).reduce(
          (formattedRecordsPendingChanges, [recordId, outerPendingChanges]) => {
            formattedRecordsPendingChanges[recordId] = {
              ...acc[key]?.[recordId],
              ...outerPendingChanges,
            };

            return formattedRecordsPendingChanges;
          },
          {},
        ),
      };

      return acc;
    }, {});
  },
);
